/**
 * Progress of a running job: a label, the percentage and a bar. `value` is
 * 0–100; leave it out for work with no measurable progress.
 *
 * `role="progressbar"` with the value, so assistive tech can follow a run
 * that takes minutes.
 */

import { LoaderCircle } from 'lucide-react'
import type { ReactNode } from 'react'

interface ProgressBarProps {
    label?: ReactNode
    value?: number
    /** Show the spinner; false for an idle or finished bar. */
    busy?: boolean
    className?: string
}

export default function ProgressBar({ label, value, busy = true, className }: ProgressBarProps) {
    const known = typeof value === 'number' && Number.isFinite(value)
    const percent = known ? Math.max(0, Math.min(100, Math.round(value))) : 0
    return (
        <div className={className}>
            {(label || known) && (
                <div className="mb-2 flex items-center justify-between gap-3 text-xs font-semibold text-fg-secondary">
                    <span className="flex min-w-0 items-center gap-2">
                        {busy && (
                            <LoaderCircle className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" aria-hidden="true" />
                        )}
                        <span className="truncate" aria-live="polite">{label}</span>
                    </span>
                    {/* German style: "45 %", never broken across lines. */}
                    {known && <span className="tabular-nums">{`${percent}\u00a0%`}</span>}
                </div>
            )}
            <div
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={known ? percent : undefined}
                aria-label={typeof label === 'string' ? label : 'Fortschritt'}
                className="h-2 overflow-hidden rounded-full bg-surface-sunken"
            >
                <div
                    className={`h-full rounded-full bg-primary transition-[width] duration-500 ease-out ${
                        known ? '' : 'w-1/3 animate-progress-indeterminate'
                    }`}
                    style={known ? { width: `${percent}%` } : undefined}
                />
            </div>
        </div>
    )
}
